import api from './client'
import type { TabelaRow } from '../components/tabelas/GenericTabelaPage'

// ── Helper: adapta resposta { cdXXX, nmXXX, status } → TabelaRow ─────
function toRow(cd: string, nm: string, status: string): TabelaRow { return { cd, nm, status } }

// ── Marca ────────────────────────────────────────────────────────────
export const marcasApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<TabelaRow[]> =>
    api.get('/v1/tabelas/marcas', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => toRow(x.cdmarc, x.nmmarc, x.status))),
  create: (empresaId: number, cd: string, nm: string, status: string): Promise<TabelaRow> =>
    api.post('/v1/tabelas/marcas', { cdmarc: cd, nmmarc: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdmarc, r.data.nmmarc, r.data.status)),
  update: (empresaId: number, cd: string, nm?: string, status?: string): Promise<TabelaRow> =>
    api.put(`/v1/tabelas/marcas/${cd}`, { nmmarc: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdmarc, r.data.nmmarc, r.data.status)),
}

// ── Famílias (fams) ───────────────────────────────────────────────────
export const familiasApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<TabelaRow[]> =>
    api.get('/v1/tabelas/familias', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => toRow(x.cdfam, x.nmfam, x.status))),
  create: (empresaId: number, cd: string, nm: string, status: string): Promise<TabelaRow> =>
    api.post('/v1/tabelas/familias', { cdfam: cd, nmfam: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdfam, r.data.nmfam, r.data.status)),
  update: (empresaId: number, cd: string, nm?: string, status?: string): Promise<TabelaRow> =>
    api.put(`/v1/tabelas/familias/${cd}`, { nmfam: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdfam, r.data.nmfam, r.data.status)),
}

// ── Linhas (lins) ─────────────────────────────────────────────────────
export const linhasApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<TabelaRow[]> =>
    api.get('/v1/tabelas/linhas', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => toRow(x.cdlin, x.nmlin, x.status))),
  create: (empresaId: number, cd: string, nm: string, status: string): Promise<TabelaRow> =>
    api.post('/v1/tabelas/linhas', { cdlin: cd, nmlin: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdlin, r.data.nmlin, r.data.status)),
  update: (empresaId: number, cd: string, nm?: string, status?: string): Promise<TabelaRow> =>
    api.put(`/v1/tabelas/linhas/${cd}`, { nmlin: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdlin, r.data.nmlin, r.data.status)),
}

// ── Mercados (mercs) ──────────────────────────────────────────────────
export const mercadosApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<TabelaRow[]> =>
    api.get('/v1/tabelas/mercados', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => toRow(x.cdmerc, x.nmmerc, x.status))),
  create: (empresaId: number, cd: string, nm: string, status: string): Promise<TabelaRow> =>
    api.post('/v1/tabelas/mercados', { cdmerc: cd, nmmerc: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdmerc, r.data.nmmerc, r.data.status)),
  update: (empresaId: number, cd: string, nm?: string, status?: string): Promise<TabelaRow> =>
    api.put(`/v1/tabelas/mercados/${cd}`, { nmmerc: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdmerc, r.data.nmmerc, r.data.status)),
}

// ── Regiões (regis) ───────────────────────────────────────────────────
export const regioesApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<TabelaRow[]> =>
    api.get('/v1/tabelas/regioes', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => toRow(x.cdregi, x.nmregi, x.status))),
  create: (empresaId: number, cd: string, nm: string, status: string): Promise<TabelaRow> =>
    api.post('/v1/tabelas/regioes', { cdregi: cd, nmregi: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdregi, r.data.nmregi, r.data.status)),
  update: (empresaId: number, cd: string, nm?: string, status?: string): Promise<TabelaRow> =>
    api.put(`/v1/tabelas/regioes/${cd}`, { nmregi: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdregi, r.data.nmregi, r.data.status)),
}

// ── Unidades (unids) — 7 colunas oficiais UnidsGridFrame ──────────────
export interface UnidadeRow {
  cdunid: string
  nmunid: string
  status: string
  cdunidnfe: string | null
  qtdecasasdec: number
  cdunidpai: string | null
  fatorunidpai: number
}

export const unidadesApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<UnidadeRow[]> =>
    api.get('/v1/tabelas/unidades', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => ({
        cdunid: x.cdunid,
        nmunid: x.nmunid,
        status: x.status,
        cdunidnfe: x.cdunidnfe,
        qtdecasasdec: x.qtdecasasdec,
        cdunidpai: x.cdunidpai,
        fatorunidpai: x.fatorunidpai
      }))),
  create: (empresaId: number, data: UnidadeRow): Promise<UnidadeRow> =>
    api.post('/v1/tabelas/unidades', data, { params: { empresa_id: empresaId } })
      .then(r => r.data),
  update: (empresaId: number, cd: string, data: Partial<Omit<UnidadeRow, 'cdunid'>>): Promise<UnidadeRow> =>
    api.put(`/v1/tabelas/unidades/${cd}`, data, { params: { empresa_id: empresaId } })
      .then(r => r.data),
}

// ── Coleções (colecs) ─────────────────────────────────────────────────
export const colecoesApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<TabelaRow[]> =>
    api.get('/v1/tabelas/colecoes', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => toRow(x.cdcolec, x.nmcolec, x.status))),
  create: (empresaId: number, cd: string, nm: string, status: string): Promise<TabelaRow> =>
    api.post('/v1/tabelas/colecoes', { cdcolec: cd, nmcolec: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdcolec, r.data.nmcolec, r.data.status)),
  update: (empresaId: number, cd: string, nm?: string, status?: string): Promise<TabelaRow> =>
    api.put(`/v1/tabelas/colecoes/${cd}`, { nmcolec: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdcolec, r.data.nmcolec, r.data.status)),
}

// ── Cores (cores) ─────────────────────────────────────────────────────
export const coresApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<TabelaRow[]> =>
    api.get('/v1/tabelas/cores', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => toRow(x.cdcor, x.nmcor, x.status))),
  create: (empresaId: number, cd: string, nm: string, status: string): Promise<TabelaRow> =>
    api.post('/v1/tabelas/cores', { cdcor: cd, nmcor: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdcor, r.data.nmcor, r.data.status)),
  update: (empresaId: number, cd: string, nm?: string, status?: string): Promise<TabelaRow> =>
    api.put(`/v1/tabelas/cores/${cd}`, { nmcor: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdcor, r.data.nmcor, r.data.status)),
}

// ── Tamanhos (tams) ───────────────────────────────────────────────────
export const tamanhosApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<TabelaRow[]> =>
    api.get('/v1/tabelas/tamanhos', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => toRow(x.cdtam, x.nmtam, x.status))),
  create: (empresaId: number, cd: string, nm: string, status: string): Promise<TabelaRow> =>
    api.post('/v1/tabelas/tamanhos', { cdtam: cd, nmtam: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdtam, r.data.nmtam, r.data.status)),
  update: (empresaId: number, cd: string, nm?: string, status?: string): Promise<TabelaRow> =>
    api.put(`/v1/tabelas/tamanhos/${cd}`, { nmtam: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdtam, r.data.nmtam, r.data.status)),
}

// ── Tipos de Documento (tipdocs) ──────────────────────────────────────
export const tiposDocumentoApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<TabelaRow[]> =>
    api.get('/v1/tabelas/tipos-documento', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => toRow(x.cdtipdoc, x.nmtipdoc, x.status))),
  create: (empresaId: number, cd: string, nm: string, status: string): Promise<TabelaRow> =>
    api.post('/v1/tabelas/tipos-documento', { cdtipdoc: cd, nmtipdoc: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdtipdoc, r.data.nmtipdoc, r.data.status)),
  update: (empresaId: number, cd: string, nm?: string, status?: string): Promise<TabelaRow> =>
    api.put(`/v1/tabelas/tipos-documento/${cd}`, { nmtipdoc: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdtipdoc, r.data.nmtipdoc, r.data.status)),
}

// ── Categorias (cats) ─────────────────────────────────────────────────
export const categoriasApi = {
  list: (empresaId: number, skip = 0, limit = 500): Promise<TabelaRow[]> =>
    api.get('/v1/tabelas/categorias', { params: { empresa_id: empresaId, skip, limit } })
      .then(r => r.data.map((x: any) => toRow(x.cdcat, x.nmcat, x.status))),
  create: (empresaId: number, cd: string, nm: string, status: string): Promise<TabelaRow> =>
    api.post('/v1/tabelas/categorias', { cdcat: cd, nmcat: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdcat, r.data.nmcat, r.data.status)),
  update: (empresaId: number, cd: string, nm?: string, status?: string): Promise<TabelaRow> =>
    api.put(`/v1/tabelas/categorias/${cd}`, { nmcat: nm, status }, { params: { empresa_id: empresaId } })
      .then(r => toRow(r.data.cdcat, r.data.nmcat, r.data.status)),
}

